<script setup lang="ts">
import { computed } from 'vue';
import { AkIcon } from '@ankorstore/design-system';
import { getLang } from '@core/utilities/lang';
import { SeePriceCta } from '@bc/shared/components';

defineProps<{
  product: {
    price: string;
    discountedPrice?: string;
    variantsLabel: string;
    unitsLabel: string;
    retailPrice: string;
  };
  showPrice: boolean;
  /** PRICE-2: hide low-value labels (units, variants) on the simplified tile. */
  simplified?: boolean;
}>();

const isGerman = computed(() => getLang() === 'de');
</script>

<template>
  <div class="card-product-price">
    <div
      v-if="!simplified"
      class="card-product-price__variants-label"
    >
      <span v-if="product.variantsLabel">{{ product.variantsLabel }}</span>
    </div>

    <p v-if="showPrice">
      <span
        v-if="product.discountedPrice"
        class="card-product-price__discounted-price"
      >
        {{ product.discountedPrice }}
      </span>

      <span
        :class="{
          'card-product-price__price': true,
          'card-product-price__price--discounted': product.discountedPrice,
        }"
      >
        {{ product.price }}
      </span>
    </p>
    <SeePriceCta v-else>
      <template #default="{ openModal, message }">
        <div
          :class="{
            'card-product-price__see-price-cta': true,
            'card-product-price__see-price-cta--break-words': isGerman,
          }"
          data-testid="price-link"
          @click.stop.prevent="openModal"
        >
          <AkIcon
            ref="iconLock"
            symbol="lock"
          />
          <span>{{ message }}</span>
        </div>
      </template>
    </SeePriceCta>

    <div class="card-product-price__sub-infos sub-infos">
      <div
        v-if="showPrice && !simplified"
        class="card-product-price__units-label"
      >
        {{ product.unitsLabel }}
      </div>

      <div class="card-product-price__price-retail">
        {{ $t('SRP') }}&nbsp;{{ product.retailPrice }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-product-price {
  @apply ds-flex ds-flex-1 ds-mt-2 ds-gap-1 ds-flex-col;
  $root: &;

  &__variants-label {
    @apply ds-text-xs;
    color: theme('colors.accent.700');
    min-height: 16px;
  }

  &__discounted-price {
    @apply ds-text-base;
    color: theme('colors.discount.700');
    font-weight: theme('fontWeight.bold');
    margin-right: theme('spacing.1');
  }

  &__price {
    @apply ds-text-base;
    color: theme('colors.primary');
    font-weight: theme('fontWeight.bold');

    &--discounted {
      color: theme('colors.neutral.700');
      font-weight: theme('fontWeight.normal');
      text-decoration: line-through;
    }
  }

  &__see-price-cta {
    display: flex;
    align-items: baseline;
    color: theme('colors.info.700');
    font-weight: theme('fontWeight.bold');
    cursor: pointer;

    &--break-words {
      word-break: break-all;
    }
  }

  &__units-label,
  &__price-retail {
    @apply ds-text-xs;
    font-weight: theme('fontWeight.semibold');
    color: theme('colors.neutral.700');
  }

}

.sub-infos {
  @apply ds-flex ds-flex-col sm:ds-flex-row ds-justify-between ds-flex-wrap;
  &__text {
    @apply ds-text-neutral-700;
  }
}
</style>
